import { Component, type ErrorInfo, type ReactNode } from 'react';
import { registrarError } from '../../domain/errores';

interface Props {
  children: ReactNode;
}

interface Estado {
  error: Error | null;
  componente: string | null;
}

/**
 * Última red bajo la aplicación.
 *
 * Sin ella, cualquier fallo al renderizar deja la pantalla en blanco y sin
 * explicación: React desmonta el árbol entero y el único rastro queda en la
 * consola del navegador, donde nadie mira. Con ella, el fallo se ve, se puede
 * leer y se puede copiar para reportarlo.
 *
 * Tiene que ser una clase: los ganchos no pueden capturar errores de render.
 */
export class BarreraError extends Component<Props, Estado> {
  override state: Estado = { error: null, componente: null };

  static getDerivedStateFromError(error: Error): Partial<Estado> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    registrarError('app.render', error);
    this.setState({ componente: info.componentStack ?? null });
  }

  override render(): ReactNode {
    const { error, componente } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          padding: '2rem 1.5rem',
          background: 'var(--bg, #f1f5f9)',
          color: 'var(--text-1, #0f172a)',
          fontFamily: 'var(--font-body, system-ui, sans-serif)',
        }}
      >
        <div style={{ maxWidth: '46rem', width: '100%' }}>
          <h1 style={{ fontSize: '1.35rem', margin: '0 0 0.5rem' }}>
            Algo se rompió al dibujar la pantalla
          </h1>

          <p style={{ margin: '0 0 1rem', lineHeight: 1.55, color: 'var(--text-2, #475569)' }}>
            No es culpa tuya ni se ha perdido nada. Recarga la página; si vuelve a pasar, copia el
            detalle de abajo y pásaselo a quien mantiene la consola.
          </p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '0.55rem 1.1rem',
              marginBottom: '1.25rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: 'var(--accent, #2563eb)',
              color: '#fff',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Recargar
          </button>

          <pre
            style={{
              margin: 0,
              padding: '0.9rem',
              maxHeight: '18rem',
              overflow: 'auto',
              borderRadius: '0.5rem',
              background: 'var(--superficie-elevada, #fff)',
              border: '1px solid var(--border, #e2e8f0)',
              fontSize: '0.8rem',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
            {componente ? `\n\nEn el componente:${componente}` : ''}
          </pre>
        </div>
      </div>
    );
  }
}
